import { faRotateLeft, faTag } from '@fortawesome/pro-light-svg-icons'
import { Fragment, useCallback, useMemo, useState } from 'react'
import { DisplayPriceGettingSpecificPriceParams } from '../../../../baseComponents/Displays/DisplayPrice'
import { permissionCheck } from '../../../../baseComponents/PermissionChecker/function'
import { TwinTableLink } from '../../../../baseComponents/TwinTable'
import { TableComponentProps } from '../../../../baseComponents/TwinTable/BaseScreenTable/types'
import { CustomRenderString, DefaultTableCustomRender, CustomRenderBoolean, EditRow, DeleteRow, CustomRenderImageToolTip } from '../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import useProductActiveDictionary from '../../../../utils/hooks/product/useProductActiveDictionary'
import useTwinTranslation from '../../../../utils/hooks/useTwinTranslation'
import { getAllSpecificPriceRules, getConfigParam } from '../../../../utils/reducers/getters'
import { ProductExtendedTyLong } from '../../types'
import { CategoriesPayload } from '../../../../utils/reducers/products/categories'
import { BrandsPayload } from '../../../../utils/reducers/products/brands'
import { OnRowClick } from '../../../../baseComponents/TwinTable/VirtualTableListing/types'
import locations from '../../../../utils/globals/locations'
import useEditDeleteModal from '../../../../utils/hooks/useEditDeleteModal'
import { ITIconToolTip } from '../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays'
import ModalPrintProductTags from '../../ProductLayout/Subcomponents/ModalPrintProductTags'
import CustomSelect from '../../../../forms/CustomSelect'
import { TableInstanceType } from '../../../../baseComponents/TwinTable/types'
import { CollectionsPayload } from '../../../../utils/reducers/products/collections'

type TableProductsProps = TableComponentProps & {
    categories: CategoriesPayload
    brands: BrandsPayload
    collections: CollectionsPayload
    onRowClick?: OnRowClick
}

const TableProducts: React.FC<TableProductsProps> = ({ setTableInstance, userPermissions, setOpened, categories, brands, collections, onRowClick, tableInstance }) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const { t } = useTwinTranslation()
    const { productActiveDict } = useProductActiveDictionary()
    const { handleTypeDisplayProducts, extraWhereParams, defaultSpecificPriceRuleId, dictTypeDisplayProducts, specificPriceRules, myTypeDisplayProducts } = useTableProductsLogic({ tableInstance })

    const columns: ColumnTableSchema<ProductExtendedTyLong> = [
        {
            id: 'ProductImages.url',
            dataKey: 'ProductImages.url',
            label: t('image', 'Imagen'),
            width: 75,
            customRender: (parameterValue, allRowData) => <CustomRenderImageToolTip className='table_products_image' value={parameterValue} allRowData={allRowData} />
        },
        {
            id: 'id',
            dataKey: 'id',
            label: t('id', 'ID'),
            sortable: true,
            width: 75,
            sortKey: 'Product"."id'
        },
        {
            id: 'reference',
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('reference', 'Referencia') }
            },
            sortable: true,
            width: 140,
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />
        },
        {
            id: 'name',
            dataKey: 'ProductLangs.name',
            label: t('name', 'Nombre'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            },
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            minWidth: 130
        },
        {
            id: 'price',
            dataKey: 'SpecificPriceRuleId',
            label: (props) => {
                const myValue = parseInt(props.selectedFilterValue || props.value || defaultSpecificPriceRuleId)
                return <span>{t('price', 'Precio') + ' (' + (specificPriceRules?.dictionary?.[myValue]?.name || t('notSelectedPriceRule', 'Sin tarifa selec.')) + ')'} </span>
            },
            simpleLabel: t('price', 'Precio'),
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: { items: specificPriceRules?.dictionary || {}, label: t('specificPriceRule', 'Tarifa') }
            },
            generalSearchable: false,
            customRender: (_parameterValue, allRowData) => {
                return <DisplayPriceGettingSpecificPriceParams value={allRowData?.calculated_price} CurrencyId={specificPriceRules?.dictionary[allRowData.SpecificPriceRuleId]?.CurrencyId || ''} with_vat={false} specificPriceRules={specificPriceRules} SpecificPriceRuleId={defaultSpecificPriceRuleId} />
            },
            minWidth: 150
        },
        {
            id: 'CategoryId',
            dataKey: 'CategoriesProducts.CategoryId',
            label: t('category', 'Categoría'),
            sortable: true,
            customRender: (parameterValue) => {
                return <DefaultTableCustomRender value={parameterValue} items={categories.dictionary} fieldName='CategoryLangs.name' />
            },
            minWidth: 130,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: categories.dictionary, label: t('category', 'Categoría'), fieldName: 'CategoryLangs.name' }
            },
            generalSearchable: false
        },
        {
            id: 'BrandId',
            dataKey: 'BrandId',
            label: t('brand', 'Marca'),
            minWidth: 110,
            sortable: true,
            customRender: (parameterValue) => <DefaultTableCustomRender value={parameterValue} items={brands} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: brands, label: t('brand', 'Marca') }
            },
            generalSearchable: false
        },
        {
            id: 'CollectionId',
            dataKey: 'CollectionId',
            label: t('collection', 'Colección'),
            minWidth: 110,
            sortable: true,
            customRender: (parameterValue) => <DefaultTableCustomRender value={parameterValue} items={collections} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: collections, label: t('collection', 'Colección') }
            },
            generalSearchable: false
        },
        {
            id: 'code_bar',
            dataKey: 'code_bar',
            label: t('codebarShort', 'Cód. barras'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('codebarShort', 'Cód. barras') }
            },
            sortable: true,
            width: 145,
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />
        },
        {
            id: 'active',
            dataKey: 'active',
            label: t('active', 'Activo'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={45} />,
            hideColumnByDefault: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: { items: productActiveDict, label: t('active', 'Activo') }
            },
            generalSearchable: false
        },
        {
            id: 'printTags',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('printTags', 'Imprimir etiquetas'),
            customRender: (parameterValue, allRowData) => <ITIconToolTip message={t('printTags', 'Imprimir etiquetas')} id={'printTags' + allRowData?.id || ''} icon={faTag} onClick={(e: any) => { e.preventDefault(); e.stopPropagation(); setOpenModal({ type: 'edit', allRowData})}} className='text-15 pt-1' />,
            hideColumnByDefault: true
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'delete', allRowData })} icon={!allRowData?.active ? faRotateLeft : undefined} messageTooltip={!allRowData?.active ? t('reactivate', 'Reactivar') : t('unable', 'Desactivar')} />,
            havePermission: permissionCheck(userPermissions, 'delete')
        }
    ]

    return (
        <Fragment>
            <TwinTableLink createRowDataLink={[locations.product, ['$id', 'main']]} name='products' columns={columns} getDataFrom='/api/app/product/getAllProductsComplete' extraWhereParams={extraWhereParams} extraWhereSubstituibleParams={{ 'SpecificPriceRuleId': defaultSpecificPriceRuleId }} setTableInstance={setTableInstance} onRowClick={onRowClick}>
                <CustomSelect label={t('showProducts','Mostrar productos')} items={dictTypeDisplayProducts} className='w-200 product_table_filters' onChange={handleTypeDisplayProducts} value={myTypeDisplayProducts} />
            </TwinTableLink>
            {openModal?.type === 'edit' ? <ModalPrintProductTags setOpened={() => setOpenModal(null)} onSubmit={() => { }} basicData={openModal.allRowData} /> : null}
        </Fragment>
    )
}

interface TableProductsLogicProps {
    tableInstance: TableInstanceType
}

const useTableProductsLogic = ({ tableInstance }: TableProductsLogicProps) => {
    const { t } = useTwinTranslation()
    const [myTypeDisplayProducts, setMyTypeDisplayProducts] = useState<string>(getConfigParam('typeDisplayProducts') || 'onlyParents')
    const specificPriceRules = getAllSpecificPriceRules()
    const dictTypeDisplayProducts = {
        'onlyParents': { name: t('onlyParents', 'Solo padres') },
        'onlyVariants': { name: t('onlyVariants', 'Solo variantes') },
        'allProducts': { name: t('allProducts', 'Todos los productos') },
    }
    const extraWhereParams = useMemo(() => {
        return { productType: { '$or': [0, 3, 4] }, typeDisplayProducts: myTypeDisplayProducts }
    }, [myTypeDisplayProducts])

    const defaultSpecificPriceRuleId = useMemo(() => {
        const specificPriceRulesSorted = specificPriceRules?.arrSorted || []
        return specificPriceRulesSorted?.[0]?.id
    }, [specificPriceRules?.arrSorted])

    const handleTypeDisplayProducts = useCallback((value: any) => {
        setMyTypeDisplayProducts(value)
        tableInstance?.getTableDataFromStart()
    }, [tableInstance])

    return { handleTypeDisplayProducts, extraWhereParams, defaultSpecificPriceRuleId, dictTypeDisplayProducts, specificPriceRules, myTypeDisplayProducts }
}


export default TableProducts